import { useCallback, useEffect, useMemo, useState } from 'react';
import type { IapScope } from '../ICSConstants';
import {
  actionColumnStorageKey,
  actionColumnVisibility,
  defaultHiddenActionColumns,
  parseHiddenActionColumns,
  toggleActionColumn,
  type ActionColumnId,
} from './actionColumnModel';

export function useActionColumnVisibility(
  scope: IapScope,
  narrow: boolean,
  options?: {
    storageKey?: string;
    defaultHidden?: readonly ActionColumnId[];
  }
) {
  const storageKey = options?.storageKey ?? actionColumnStorageKey(scope);
  const defaultHidden = options?.defaultHidden;
  const [storedHidden, setStoredHidden] = useState<ActionColumnId[] | null>(
    () => parseHiddenActionColumns(localStorage.getItem(storageKey))
  );

  useEffect(() => {
    setStoredHidden(parseHiddenActionColumns(localStorage.getItem(storageKey)));
  }, [storageKey]);

  const hiddenColumns =
    storedHidden ??
    (defaultHidden ? [...defaultHidden] : defaultHiddenActionColumns(narrow));

  const persistHidden = useCallback(
    (next: ActionColumnId[] | null) => {
      setStoredHidden(next);
      if (next) {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } else {
        localStorage.removeItem(storageKey);
      }
    },
    [storageKey]
  );

  const columnVisibility = useMemo(
    () => actionColumnVisibility(scope, hiddenColumns),
    [scope, hiddenColumns]
  );

  const onToggle = useCallback(
    (id: ActionColumnId) => {
      persistHidden(toggleActionColumn(hiddenColumns, id));
    },
    [hiddenColumns, persistHidden]
  );

  const onReset = useCallback(() => {
    persistHidden(null);
  }, [persistHidden]);

  return {
    hiddenColumns,
    columnVisibility,
    onToggle,
    onReset,
  };
}
